import { useMemo } from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useChainConfiguration } from '@occc-app/Chain/hooks';
import { Feature, useGetIsUserPermittedTo } from '@occc/utils-access-control';
import { useConfig } from '@occc/config';

import { ReferralProgramDetailsPage } from './referral-program-details.page';
import { ReferralProgramNotConfiguredPage } from './referral-program-not-configured.page';
import { referralProgramDetailsMessages } from './referral-program-details.messages';
import { breadcrumbs } from './referral-program-details.constants';
import {
  getReferralProgramPageActions,
  mapReferralProgramStatisticsToMetricsValues,
  mapReferralProgramToConfigurationDetailsProps,
} from './referral-program-details.utils';
import { useReferralProgramStatistics } from './use-referral-program-statistics.hook';

export const ReferralProgramDetailsController = () => {
  const { referralProgramConfiguration } = useChainConfiguration();
  const { config } = useConfig();
  const { statistics, loading: isStatisticsLoading } = useReferralProgramStatistics();
  const getIsUserPermittedTo = useGetIsUserPermittedTo();
  useAppLayoutContextConsumer(breadcrumbs);
  const subBrands = config.subBrands?.enabled ? config.subBrands.list : null;

  const actions = useMemo(() => {
    const isUserPermittedToEdit = getIsUserPermittedTo(Feature.REFERRAL_PROGRAM_EDIT);
    const isUserPermittedToView = getIsUserPermittedTo(Feature.REFERRAL_PROGRAM_EDIT_VIEW_ONLY);
    return getReferralProgramPageActions({
      referralProgramConfiguration,
      isUserPermittedToEdit,
      isUserPermittedToView,
    });
  }, [getIsUserPermittedTo, referralProgramConfiguration]);

  const configurationDetails = useMemo(() => {
    if (!referralProgramConfiguration) {
      return null;
    }

    return mapReferralProgramToConfigurationDetailsProps(referralProgramConfiguration, subBrands);
  }, [referralProgramConfiguration, subBrands]);

  return (
    <Page
      pageHeaderProps={{
        title: referralProgramDetailsMessages.pageTitle,
        subtitle: referralProgramDetailsMessages.pageSubtitle,
      }}
      browserTabTitle={referralProgramDetailsMessages.pageTitle}
      containerProps={{ variant: 'md' }}
      actions={actions}
    >
      {configurationDetails ? (
        <ReferralProgramDetailsPage
          senderAward={configurationDetails.senderAward}
          receiverAward={configurationDetails.receiverAward}
          qualifyingEvent={configurationDetails.qualifyingEvent}
          marketingContents={configurationDetails.marketingContents}
          metrics={{
            isLoading: isStatisticsLoading,
            values: mapReferralProgramStatisticsToMetricsValues(statistics),
            isLoadingFailed: !isStatisticsLoading && !statistics,
          }}
        />
      ) : (
        <ReferralProgramNotConfiguredPage />
      )}
    </Page>
  );
};
